import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import api, { LoginPayload } from 'api';
import client from 'api/client';
import { profilePictureKeys } from 'hooks/useMyAccount';
import { queryClient } from 'queryClient';
import { loadUserFullInfo } from '../user/user-middleware';
import storage from 'utils/storageController';
import { getErrorMessage } from 'utils/v2/errorHelper';

export const INVALID_LOGIN_MESSAGE = 'Wrong email or password';

export const authUser = createAsyncThunk(
    'app/auth_user',
    async (data: LoginPayload, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getTokens(data));
        // `getTokens` uses rejectWithValue, so a failed login does not throw.
        // Reject here as well: returning success left `auth.error` as the same
        // string on every bad password, and the login page only toasted when
        // that string changed. Also skip loadUserFullInfo — those calls have
        // no token, and their 401 runs the global logout interceptor, which
        // clears the credential error.
        if (getTokens.rejected.match(result)) {
            const message =
                typeof result.payload === 'string' && result.payload.trim()
                    ? result.payload
                    : getErrorMessage(result.error);
            return rejectWithValue(message);
        }
        await dispatch(loadUserFullInfo());
    }
);

export const getTokens = createAsyncThunk(
    'api/login',
    async (data: LoginPayload, { rejectWithValue }) => {
        try {
            const loginResp = await api.login(data);
            storage.setRefreshToken(loginResp.data.refresh_token);
            return loginResp;
        } catch (error) {
            // Credential and other failures include `msg`. The fixed rate-limit
            // sentence is only for a 429 whose body is HTML or empty.
            return rejectWithValue(getErrorMessage(error));
        }
    }
);

export const logout = createAsyncThunk('app/logout', async () => {
    storage.logout();
    delete axios.defaults.headers.common.Authorization;
    delete client.defaults.headers.common.Authorization;
    // Drop current-user profile picture so the next session cannot flash stale data
    queryClient.removeQueries({ queryKey: profilePictureKeys.all });
});
